import { useCallback, useMemo } from 'react';
import { useNativeState } from '@shared/core/native';
import { EventPageType } from '@graphql/generated-base';
import { useMigratedCustomPageFeatureFlags } from '@shared/hooks/useMigratedCustomPageFeatureFlags/useMigratedCustomPageFeatureFlags';

const WELCOME_PAGE_SLUG = 'welcome';
/**
 * Hook that provides a function to check if a page is a welcome page.
 *
 * The hook returns a `checkIsWelcomePage` function that can be used to determine if any page
 * (by slug and type) is a welcome page. This is useful when iterating over pages in maps or
 * when checking pages other than the current one.
 *
 * @returns An object containing:
 * - `checkIsWelcomePage`: A function that checks if a page is a welcome page. Returns an object with:
 *   - `isWelcomePage`: `true` if the page is a welcome page (either native `EventPageType.welcome` or
 *     a custom page with slug matching `CUSTOM_WELCOME_SLUG` when the feature flag is enabled).
 *   - `isNativeWelcomePage`: `true` if the app is in native mode and the page is a welcome page.
 * - `isWelcomePageAsCustomPageEnabled`: `true` if the feature flag for custom event pages
 *   migration is enabled and not loading.
 *
 * @example
 * ```tsx
 * const { checkIsWelcomePage, isWelcomePageAsCustomPageEnabled } = useWelcomePage();
 *
 * // Check a specific page
 * const { isWelcomePage } = checkIsWelcomePage(page.pageSlug, page.type);
 *
 * // Use in a map
 * filteredPages.map(page => {
 *   const { isWelcomePage } = checkIsWelcomePage(page.pageSlug, page.type);
 *   // ...
 * });
 * ```
 */
export const useWelcomePage = (): Readonly<{
  isWelcomePageAsCustomPageEnabled: boolean;
  checkIsWelcomePage: (
    pageSlug: string | undefined,
    pageType: EventPageType | undefined | null,
  ) => { isWelcomePage: boolean; isNativeWelcomePage: boolean; isCustomWelcomePage: boolean };
  loading: boolean;
}> => {
  const { isNative } = useNativeState();

  const { isMigratedCustomPageEnabled, isLoading: isWebsiteCustomEventPagesEnabledLoading } = useMigratedCustomPageFeatureFlags();

  const isWelcomePageAsCustomPageEnabled = useMemo(
    () => isMigratedCustomPageEnabled(WELCOME_PAGE_SLUG) && !isWebsiteCustomEventPagesEnabledLoading,
    [isMigratedCustomPageEnabled, isWebsiteCustomEventPagesEnabledLoading],
  );

  const checkIsWelcomePage = useCallback(
    (slug: string = '', type: EventPageType | undefined | null) => {
      if (isMigratedCustomPageEnabled(slug) && !isWebsiteCustomEventPagesEnabledLoading && type === EventPageType.custom && slug === WELCOME_PAGE_SLUG) {
        return { isWelcomePage: true, isNativeWelcomePage: isNative, isCustomWelcomePage: true };
      }
      return { isWelcomePage: type === EventPageType.welcome, isNativeWelcomePage: type === EventPageType.welcome && isNative, isCustomWelcomePage: false };
    },
    [isMigratedCustomPageEnabled, isWebsiteCustomEventPagesEnabledLoading, isNative],
  );

  return { isWelcomePageAsCustomPageEnabled, checkIsWelcomePage, loading: isWebsiteCustomEventPagesEnabledLoading };
};
